import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ChevronDown,
  CreditCard,
  HelpCircle,
  Import,
  LayoutDashboard,
  LayoutGrid,
  Menu,
  Package,
  Phone,
  PlayCircle,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  UserRound,
  X,
} from "lucide-react";
import useAppStore from "../store/useAppStore";
import { isAppProductManagementAllowed } from "../utils/appProductAccess";
// import { getSelectedMarketplace } from "../utils/axiosInstance";
import sidebarLogo from "../icons/sidebarlogo.png";
import smallLogo from "../icons/smallLogo.png";
import "./Sidebar.css";

const FULL_WIDTH = 252;
const ICON_WIDTH = 78;
const MOBILE_BREAKPOINT = 768;

const PRODUCT_LINKS = [
  { to: "/products", icon: Package, label: "Products" },
  { to: "/app-products", icon: Package, label: "App Products" },
  { to: "/templates", icon: LayoutGrid, label: "Templates" },
  { to: "/BusinessPolicy", icon: ShieldCheck, label: "Business Policies" },
  { to: "/profiles", icon: UserRound, label: "Profiles" },
];

const NAV_GROUPS = [
  {
    label: "Operations",
    links: [
      { to: "/orders", icon: ShoppingCart, label: "Orders" },
      { to: "/importer", icon: Import, label: "Importer" },
    ],
  },
  {
    label: "Insights",
    links: [
      { to: "/activity", icon: Activity, label: "Activities" },
      { to: "/plans", icon: CreditCard, label: "Subscription Plans" },
    ],
  },
  {
    label: "Workspace",
    links: [
      { to: "/ebay-accounts", icon: Store, label: "eBay Accounts" },
      { to: "/settings", icon: Settings, label: "Settings" },
    ],
  },
  {
    label: "Help",
    links: [
      { to: "/Faq", icon: HelpCircle, label: "Help Center" },
      { to: "/contact-us", icon: Phone, label: "Contact Us" },
    ],
  },
];

function getAccountLabel(marketplace) {
  if (marketplace === "AMAZON") return "Amazon Accounts";
  if (marketplace === "ETSY") return "Etsy Accounts";
  return "eBay Accounts";
}
function getInitialProductsOpen() {
  try {
    return localStorage.getItem("productsOpen") !== "false";
  } catch {
    return true;
  }
}

function SidebarLink({ to, icon: Icon, label, collapsed, child = false, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={to === "/dashboard"}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      onClick={onNavigate}
      className={({ isActive }) => [
        "app-sidebar__nav-link",
        child && "app-sidebar__nav-link--child",
        isActive && "app-sidebar__nav-link--active",
      ].filter(Boolean).join(" ")}
    >
      <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}

function PlanCard() {
  const remaining = 10;
  const limit = 20;
  const usedPercent = Math.min(100, Math.round(((limit - remaining) / limit) * 100));

  return (
    <div className="app-sidebar__plan-card">
      <div className="app-sidebar__plan-heading">
        <div className="app-sidebar__plan-title">
          <Sparkles size={15} strokeWidth={2} aria-hidden="true" />
          <span>Your plan</span>
        </div>
        <span className="app-sidebar__plan-badge">FREE</span>
      </div>
      <div className="app-sidebar__credits-row">
        <span>Product credits</span>
        <strong>{remaining} / {limit}</strong>
      </div>
      <div
        className="app-sidebar__progress"
        role="progressbar"
        aria-label="Product credits used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={usedPercent}
      >
        <div className="app-sidebar__progress-fill" style={{ width: `${usedPercent}%` }} />
      </div>
    </div>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen, setSidebarOpen } = useAppStore();
  // The frontend is fixed to eBay; retain the dynamic implementation below
  // commented out for a future multi-marketplace release.
  const marketplace = "EBAY";
  const appProductManagementAllowed = isAppProductManagementAllowed();
  // const [marketplace, setMarketplace] = useState(getSelectedMarketplace);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [productsOpen, setProductsOpen] = useState(getInitialProductsOpen);
  const previousCollapsed = useRef(false);

  const collapsed = !isMobile && !sidebarOpen;
  const visibleProductLinks = PRODUCT_LINKS.filter(({ to }) => (
    to !== "/app-products" || appProductManagementAllowed
  ));
  const productRouteActive = visibleProductLinks.some(({ to }) => location.pathname.startsWith(to));
  const width = isMobile ? FULL_WIDTH : (collapsed ? ICON_WIDTH : FULL_WIDTH);

  /* Marketplace change listener disabled for eBay-only mode:
  useEffect(() => {
    const handleMarketplaceChanged = (event) => {
      setMarketplace(event.detail?.marketplace || getSelectedMarketplace());
    };
    window.addEventListener("marketplaceChanged", handleMarketplaceChanged);
    return () => window.removeEventListener("marketplaceChanged", handleMarketplaceChanged);
  }, []);
  */

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setSidebarOpen]);

  useEffect(() => {
    try {
      localStorage.setItem("productsOpen", String(productsOpen));
    } catch {
      // Keep submenu state in memory when browser storage is unavailable.
    }
  }, [productsOpen]);

  useEffect(() => {
    const openProducts = () => setProductsOpen(true);
    window.addEventListener("appTour:openProducts", openProducts);
    return () => window.removeEventListener("appTour:openProducts", openProducts);
  }, []);

  useEffect(() => {
    if (collapsed && !previousCollapsed.current) setProductsOpen(false);
    previousCollapsed.current = collapsed;
  }, [collapsed]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile, setSidebarOpen]);

  const closeMobileDrawer = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const toggleProducts = () => {
    // In the collapsed rail only the parent icon is visible. Expand the rail
    // first so the product links (including Templates and Profiles) become
    // available instead of toggling an invisible submenu.
    if (collapsed) {
      setSidebarOpen(true);
      setProductsOpen(true);
      navigate("/products");
      return;
    }

    const nextOpen = !productsOpen;
    setProductsOpen(nextOpen);
    if (nextOpen) navigate("/products");
  };

  const startTour = () => {
    try {
      localStorage.removeItem("appTourShown");
    } catch {
      // The tour can still restart if local storage is disabled.
    }
    window.location.reload();
  };

  return (
    <>
      {isMobile && sidebarOpen && (
        <button
          className="app-sidebar__scrim"
          type="button"
          aria-label="Close navigation menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {isMobile && (
        <button
          className="app-sidebar__mobile-toggle"
          type="button"
          aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
      )}

      <aside
        className={[
          "app-sidebar",
          collapsed && "app-sidebar--collapsed",
          isMobile && sidebarOpen && "app-sidebar--mobile-open",
        ].filter(Boolean).join(" ")}
        style={{ width }}
        aria-label="Main navigation"
      >
        <div className="app-sidebar__brand-row">
          <button
            className="app-sidebar__brand-link"
            type="button"
            title={collapsed ? "EcstaCommerce" : undefined}
            aria-label="Go to dashboard"
            onClick={() => {
              navigate("/dashboard");
              closeMobileDrawer();
            }}
          >
            <span className="app-sidebar__brand-mark">
              <img src={collapsed ? smallLogo : sidebarLogo} alt="" />
            </span>
          </button>
        </div>

        <nav className="app-sidebar__nav" aria-label="Workspace">
          <section className="app-sidebar__section" aria-label="Overview">
            <SidebarLink
              to="/dashboard"
              icon={LayoutDashboard}
              label="Dashboard"
              collapsed={collapsed}
              onNavigate={closeMobileDrawer}
            />
          </section>

          <section className="app-sidebar__section" aria-label="Catalog">
            <button
              className={[
                "app-sidebar__products-toggle",
                (productsOpen || productRouteActive) && "app-sidebar__products-toggle--active",
              ].filter(Boolean).join(" ")}
              type="button"
              title={collapsed ? "Manage Products" : undefined}
              aria-expanded={productsOpen && !collapsed}
              aria-controls="sidebar-products-submenu"
              onClick={toggleProducts}
              onKeyDown={(event) => {
                if (collapsed && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  toggleProducts();
                  closeMobileDrawer();
                }
              }}
            >
              <span className="app-sidebar__products-label">
                <Package size={18} strokeWidth={1.9} aria-hidden="true" />
                {!collapsed && <span>Manage Products</span>}
              </span>
              {!collapsed && (
                <ChevronDown
                  className={productsOpen ? "app-sidebar__chevron app-sidebar__chevron--open" : "app-sidebar__chevron"}
                  size={15}
                  aria-hidden="true"
                />
              )}
            </button>
            <div
              className={productsOpen && !collapsed ? "app-sidebar__collapse app-sidebar__collapse--open" : "app-sidebar__collapse"}
              id="sidebar-products-submenu"
              aria-hidden={!productsOpen || collapsed}
            >
              <div className="app-sidebar__collapse-inner">
                {visibleProductLinks.map((item) => (
                  <SidebarLink
                    key={item.to}
                    {...item}
                    collapsed={collapsed}
                    child
                    onNavigate={closeMobileDrawer}
                  />
                ))}
              </div>
            </div>
          </section>

          {NAV_GROUPS.map((group) => (
            <section className="app-sidebar__section" key={group.label} aria-label={group.label}>
              {group.links.map((item) => (
                <SidebarLink
                  key={item.to}
                  {...item}
                  label={item.to === "/ebay-accounts" ? getAccountLabel(marketplace) : item.label}
                  collapsed={collapsed}
                  onNavigate={closeMobileDrawer}
                />
              ))}
            </section>
          ))}

          <button
            className="app-sidebar__tour-button"
            type="button"
            title={collapsed ? "Start app tour" : undefined}
            onClick={startTour}
          >
            <PlayCircle size={18} strokeWidth={1.9} aria-hidden="true" />
            {!collapsed && <span>Take a product tour</span>}
          </button>
        </nav>

        {!collapsed && (
          <div className="app-sidebar__footer">
            <PlanCard />
          </div>
        )}
      </aside>
    </>
  );
}

